import type { IExternalProductsState } from './types'
import type { RootState } from '@/store/types'
import type { ActionTree, ActionContext } from 'vuex'
import type {
  IExternalLicenseResponse,
  TExternalLicensesPayload,
  IExternalProductResponse,
  TSupportCertificateBaseInfo,
  TSupportCertificateErrorInfo,
  TAssignExternalLicensePayload,
  TTransferExternalLicensesPayload,
  IExternalLicensesActivateTrialPayload,
  TAddingEditingCommentExternalLicensePayload,
} from '@/api/types'

import api from '@/api'
import i18n from '@/locale'
import { App } from '@/main'
import {
  AssignExternalLicensesModel,
  AssignExternalLicensesService,
} from '@/services/AssignExternalLicensesService'
import uniq from 'lodash/uniq'
import { getFormatSupportCertificate } from '@/helpers/edition-formatted-functions'

type TExternalProductsTree = ActionTree<IExternalProductsState, RootState>
type TExternalProductsContext = ActionContext<IExternalProductsState, RootState>

export const actions: TExternalProductsTree = {
  /**
   * fetchExternalLicenses
   * ? Извлечь список внешних лицензий
   *
   * @param {TExternalProductsContext} ctx context
   * @param {TExternalLicensesPayload} payload параметры запроса внешних лицензий
   * @returns {Promise<void>}
   */
  fetchExternalLicenses: (
    { commit, dispatch, rootGetters }: TExternalProductsContext,
    payload: TExternalLicensesPayload
  ): Promise<void> => {
    const accountId =
      payload?.account_id ?? rootGetters['account/userAccountID']

    commit('SET_LOADING_PROCESS', { loading: true, name: 'external-licenses' })

    return api.lk
      .getAccountExternalLicenses(accountId)
      .then((data) => commit('SET_EXTERNAL_LICENSES', data))
      .catch((error) => {
        if (error.code === 'ECONNABORTED') {
          return dispatch('fetchExternalLicenses', payload)
        }

        return error
      })
      .finally(() => {
        commit('SET_LOADING_PROCESS', {
          loading: false,
          name: 'external-licenses',
        })
        dispatch('fetchSupportCertificates')
      })
  },

  /**
   * fetchSupportLicenses
   * ? Извлечь список сертификатов поддержки
   *
   * @param {TExternalProductsContext} ctx context
   * @returns {Promise<void>}
   */
  fetchSupportCertificates: ({
    commit,
    getters,
  }: TExternalProductsContext): Promise<void> => {
    const licenses: IExternalLicenseResponse[] =
      getters['externalLicenses'] ?? []
    const ids: string[] = licenses.map((item) =>
      [item.order_number, item.product_id, item.edition_id].join(';')
    )
    const uniqueIds: string[][] = uniq(ids).map((item) => item.split(';'))
    const promises = uniqueIds.map((item: string[]) => {
      const [orderNumber, productId, editionId] = item.map(Number)
      const baseInfo: TSupportCertificateBaseInfo = {
        edition_id: editionId,
        order_number: orderNumber,
        product_id: productId,
      }

      if (orderNumber === 0) {
        return null
      }

      return api.lk
        .getSupportCertificate(orderNumber, productId, editionId)
        .then((item) => getFormatSupportCertificate(item, baseInfo))
        .catch((error): TSupportCertificateErrorInfo | null => {
          if (error?.response?.status === 500) {
            return {
              ...baseInfo,
              status: 'error',
            }
          }
          return null
        })
    })

    commit('SET_LOADING_PROCESS', {
      loading: true,
      name: 'support-certificates',
    })

    return Promise.all(promises)
      .then((data) =>
        commit(
          'SET_SUPPORT_CERTIFICATES',
          data.filter((item) => item !== null)
        )
      )
      .finally(() => {
        commit('SET_LOADING_PROCESS', {
          loading: false,
          name: 'support-certificates',
        })
      })
  },

  /**
   * transferExternalLicenses
   * ? Передать лицензии другому пользователю
   *
   * @param {TExternalProductsContext} ctx context
   * @param {TTransferExternalLicensesPayload} payload данные лицензий и пользователя
   * @returns {Promise<void>}
   */
  transferExternalLicenses: (
    { commit }: TExternalProductsContext,
    payload: TTransferExternalLicensesPayload
  ): Promise<void> => {
    commit('SET_LOADING_PROCESS', {
      loading: true,
      name: 'transfer-external-licenses',
    })

    return api.lk
      .transferExternalLicenses(payload.account_id, payload.license_ids)
      .finally(() => {
        commit('SET_LOADING_PROCESS', {
          loading: false,
          name: 'transfer-external-licenses',
        })
      })
  },

  /**
   * fetchExternalUserProducts
   * ? Извлечь список внешний продуктов
   *
   * @param {TExternalProductsContext} ctx context
   * @returns {Promise<void>}
   */
  fetchExternalUserProducts: ({
    commit,
    rootGetters,
  }: TExternalProductsContext): Promise<void> => {
    if (!rootGetters['account/isUserLoggedIn']) {
      return Promise.resolve()
    }

    return api.lk.getUserExternalProducts().then((products) => {
      commit('SET_HAS_TRIAL_LICENSE', products)
      commit('SET_HAS_EXTERNAL_LICENSES', products)
    })
  },

  assignLicense(
    { commit, rootGetters, dispatch }: TExternalProductsContext,
    payload: TAssignExternalLicensePayload
  ): Promise<void> {
    const model = AssignExternalLicensesModel.fromPayload(payload)

    if (
      rootGetters['account/isRoleBusinessUser'] === false &&
      rootGetters['account/userRole'] !== null
    ) {
      App.$notify({
        type: 'info',
        message: i18n
          .t(
            'component.notify.message-error-invalid-account-for-assign-license'
          )
          .toString(),
      })
      return Promise.resolve()
    }

    commit('SET_LOADING_PROCESS', {
      loading: true,
      name: 'assign-external-license',
    })

    return (
      AssignExternalLicensesService.assign(model)
        // Получаем лицензии для проверки наличия триальных и внешних лицензий
        .then(() => dispatch('fetchExternalUserProducts'))
        .catch(() => {
          App.$notify({
            type: 'error',
            message: i18n
              .t('component.notify.message-assign-license-error')
              .toString(),
          })
        })
        .finally(() =>
          commit('SET_LOADING_PROCESS', {
            loading: false,
            name: 'assign-external-license',
          })
        )
    )
  },

  /**
   * fetchExternalProductById
   * ? Извлечь продукт по его идентификатору
   *
   * @param {TExternalProductsContext} ctx context
   * @param {string} id идентификатор продукта
   * @returns {Promise<IExternalProductResponse>} продукт
   */
  fetchExternalProductById: (
    { commit }: TExternalProductsContext,
    id: number
  ): Promise<IExternalProductResponse> => {
    commit('SET_LOADING_PROCESS', { loading: true, name: 'external-product' })

    return api.lk.getExternalProductById(id).finally(() => {
      commit('SET_LOADING_PROCESS', {
        loading: false,
        name: 'external-product',
      })
    })
  },

  /**
   * fetchExternalDownloadUrls
   * ? Заполнить список ссылок для скачивания дистрибутивов
   *
   * @param {TExternalProductsContext} ctx context
   * @returns {Promise<void>}
   */
  fetchExternalDownloadUrls: ({
    commit,
  }: TExternalProductsContext): Promise<void> => {
    commit('SET_LOADING_PROCESS', {
      loading: true,
      name: 'download-urls',
    })

    return api.lk
      .getExternalDownloadUrls()
      .then((urls) => {
        commit('SET_DOWNLOAD_URLS', urls)
      })
      .finally(() => {
        commit('SET_LOADING_PROCESS', {
          loading: false,
          name: 'download-urls',
        })
      })
  },

  /**
   * activateTrial
   * ? Активировать триальную лицензию
   *
   * @param {TExternalProductsContext} ctx context
   * @param {IExternalLicensesActivateTrialPayload} payload параметры на активацию пробной версии
   * @returns {Promise<void>}
   */
  activateTrial: async (
    { commit, dispatch }: TExternalProductsContext,
    payload: IExternalLicensesActivateTrialPayload
  ): Promise<void> => {
    commit('SET_LOADING_PROCESS', {
      loading: true,
      name: 'activate-trial-external-license',
    })

    return (
      api.lk
        .activateTrialExternalLicense(payload)
        // Получаем лицензии для проверки наличия триальных и внешних лицензий
        .then(() => dispatch('fetchExternalUserProducts'))
        .then(() => dispatch('fetchExternalLicenses'))
        .finally(() => {
          commit('SET_LOADING_PROCESS', {
            loading: false,
            name: 'activate-trial-external-license',
          })
        })
    )
  },

  /**
   * addOrEditCommentToExternalLicense
   * ? Добавить или редактировать комментарий к внешней лицензии (для бизнес аккаунта)
   *
   * @param {TExternalProductsContext} ctx context
   * @param {TAddingEditingCommentExternalLicensePayload} payload данные запроса на добавление или редактировать комментарий для внешней лицензии
   * @returns {Promise<void>}
   */
  addOrEditCommentToExternalLicense: async (
    { commit, dispatch }: TExternalProductsContext,
    payload: TAddingEditingCommentExternalLicensePayload
  ): Promise<void> => {
    commit('SET_LOADING_PROCESS', {
      loading: true,
      name: 'add-or-edit-comment-external-license',
    })

    return api.lk
      .addOrEditCommentToExternalLicense(payload)
      .then(() => dispatch('fetchExternalLicenses'))
      .finally(() =>
        commit('SET_LOADING_PROCESS', {
          loading: false,
          name: 'add-or-edit-comment-external-license',
        })
      )
  },
}
